import { Component, EventEmitter, Input, Output, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ReplaySubject, Subscription, Subject } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { ApplianceGroup, DataRefsByKey } from 'src/app/shared/models/dataref.model';
import { BinariesService } from '@services/binaries/binaries.service';
import { NotificationService } from '@services/notification.service';
import { BinaryLite } from '@shared/models/binaries/binary-lite.model';
import { PackLite } from '@shared/models/generic/pack-lite.model';
import { hasTargetDomainForBinaries } from '@packs/utils/pack.utils';

@Component({
  selector: 'app-pack-binaries-list',
  templateUrl: './pack-binaries-list.component.html',
  styleUrls: ['./pack-binaries-list.component.scss'],
  standalone: false,
})
export class PackBinariesListComponent implements OnInit, OnDestroy, OnChanges {
  @Input() pack: PackLite = {} as PackLite;
  @Input() shouldReload$: ReplaySubject<void> = new ReplaySubject<void>();
  @Output() binaryModified$: EventEmitter<void> = new EventEmitter();

  publishBinary$ = new Subject<BinaryLite>();
  deleteBinary$ = new Subject<BinaryLite>();
  readonly displayedColumns: string[] = ['applianceGroup', 'type', 'creationDate', 'createdBy', 'status', 'actions'];
  availableApplianceGroups: DataRefsByKey<ApplianceGroup> = {};
  packKey = '';
  // utily field used to prevent from updating input field "pack" binaries
  binaries: BinaryLite[] = [];
  private readonly subscription: Subscription = new Subscription();

  constructor(
    private readonly binariesService: BinariesService,
    private readonly notificationsService: NotificationService,
  ) {}

  @Input() set applianceGroupsRefData(applianceGroupsRefData: DataRefsByKey<ApplianceGroup> | null) {
    if (applianceGroupsRefData) {
      this.availableApplianceGroups = applianceGroupsRefData;
    }
  }

  ngOnInit(): void {
    this.subscription.add(this.subscribeOnBinaryPublication());
    this.subscription.add(this.subscribeOnBinaryDeletion());
    this.subscription.add(this.subscribeOnShouldReload());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pack?.currentValue?.key) {
      // reload binaries list if pack changed an pack as a key provided
      this.packKey = changes.pack.currentValue.key;
      this.reloadBinaries();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Call backend api to load pack's binaries in Lite format (BinaryLite)
   */
  public reloadBinaries(): void {
    if (this.packKey && hasTargetDomainForBinaries(this.pack)) {
      this.binariesService.getListForPack({ packId: this.packKey }).subscribe({
        next: (reloadedBinaries) => (this.binaries = reloadedBinaries),
        error: (err) => this.notificationsService.sendError('BINARIES.ERRORS.LOAD', err),
      });
    }
  }

  private subscribeOnShouldReload(): Subscription {
    return this.shouldReload$.subscribe(() => {
      this.reloadBinaries();
    });
  }

  private subscribeOnBinaryPublication(): Subscription {
    return this.publishBinary$
      .pipe(
        mergeMap((binary) =>
          this.binariesService.publishForPack({
            packId: this.packKey,
            binaryId: binary.identifier,
          }),
        ),
        tap(() => {
          this.notificationsService.sendSuccess('BINARIES.UPDATED');
          this.binaryModified$.emit();
        }),
      )
      .subscribe();
  }

  private subscribeOnBinaryDeletion(): Subscription {
    return this.deleteBinary$
      .pipe(
        mergeMap((binary) =>
          this.binariesService.deleteForPack({
            packId: this.packKey,
            binaryId: binary.identifier,
          }),
        ),
        tap(() => {
          this.notificationsService.sendSuccess('BINARIES.DELETED');
          this.binaryModified$.emit();
        }),
      )
      .subscribe();
  }
}
